import React, { useEffect, useState } from "react";
import { Plus, Search, X, Edit2, ListRestart, Trash2, User, Users } from "lucide-react";
import ClientForm from "../components/forms/ClientForm";
import ConfirmDialog from "../components/common/ConfirmDialog";
import PageHeader from "../components/common/PageHeader";
import DataTable from "../components/common/DataTable";
import EmptyState from "../components/common/EmptyState";
import Pagination from "../components/common/Pagination";
import useAlert from "../components/hook/useAlert";
import callApi from "../helpers/networkHelper";
import API_CONFIG from "../config/apiConstant";
import { parsePhoneNumberFromString } from "libphonenumber-js";

const PAGE_SIZE = 10;

const buildQueryString = (params) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== "") {
            query.append(key, value);
        }
    });
    return query.toString();
};

const getReadableErrorMessage = (error) => {
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.message) return error.message;
    return "Something went wrong. Please try again.";
};

// Display only
const formatPhone = (phone) => {
    if (!phone) return "—";
    const phoneObj = parsePhoneNumberFromString(phone);
    return phoneObj ? phoneObj.formatInternational() : phone;
};

const Client = () => {
    const { showSuccess, showError } = useAlert();

    const [clients, setClients] = useState([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [deletingClient, setDeletingClient] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const totalPages = Math.ceil(total / PAGE_SIZE);

    const fetchClients = async (initial = false) => {
        initial ? setIsPageLoading(true) : setIsTableLoading(true);

        try {
            const query = buildQueryString({
                page,
                limit: PAGE_SIZE,
                search,
                isActive:
                    statusFilter === "all"
                        ? undefined
                        : statusFilter === "active"
            });

            const res = await callApi(
                API_CONFIG.CLIENT.GET.METHOD,
                `${API_CONFIG.CLIENT.GET.ENDPOINT}?${query}`
            );

            setClients(res.data.data);
            setTotal(res.data.total);
        } catch (err) {
            showError(getReadableErrorMessage(err));
        } finally {
            setIsPageLoading(false);
            setIsTableLoading(false);
        }
    };

    useEffect(() => {
        fetchClients(true);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!isPageLoading) fetchClients();
        // eslint-disable-next-line
    }, [page, search, statusFilter]);

    const handleSubmitClient = async (data) => {
        setIsSubmitting(true);
        try {
            if (editingClient) {
                await callApi(
                    API_CONFIG.CLIENT.UPDATE.METHOD,
                    API_CONFIG.CLIENT.UPDATE.ENDPOINT.replace(":id", editingClient._id),
                    data
                );
                showSuccess("Client updated successfully");
            } else {
                await callApi(
                    API_CONFIG.CLIENT.CREATE.METHOD,
                    API_CONFIG.CLIENT.CREATE.ENDPOINT,
                    data
                );
                showSuccess("Client created successfully");
            }

            setIsModalOpen(false);
            setEditingClient(null);
            fetchClients();
        } catch (err) {
            showError(getReadableErrorMessage(err));
        } finally {
            setIsSubmitting(false);
        }
    };

    const toggleStatus = async (client) => {
        try {
            await callApi(
                API_CONFIG.CLIENT.TOGGLE_STATUS.METHOD,
                API_CONFIG.CLIENT.TOGGLE_STATUS.ENDPOINT.replace(":id", client._id),
                { isActive: !client.isActive }
            );
            showSuccess("Client status updated");
            fetchClients();
        } catch (err) {
            showError(getReadableErrorMessage(err));
        }
    };

    const handleDeleteClient = async () => {
        if (!deletingClient) return;

        setIsDeleting(true);
        try {
            await callApi(
                API_CONFIG.CLIENT.DELETE.METHOD,
                API_CONFIG.CLIENT.DELETE.ENDPOINT.replace(":id", deletingClient._id)
            );

            showSuccess(`Client "${deletingClient.clientName}" deleted`);
            setDeletingClient(null);

            // Removing the last row on a page would otherwise leave the table
            // empty on a page that no longer exists; stepping back refetches.
            if (clients.length === 1 && page > 1) {
                setPage(page - 1);
            } else {
                fetchClients();
            }
        } catch (err) {
            // The backend refuses while licenses still reference the client, so
            // the dialog stays open with the reason showing.
            showError(getReadableErrorMessage(err));
        } finally {
            setIsDeleting(false);
        }
    };

    const hasActiveFilters = search.trim() !== "" || statusFilter !== "all";

    const clearFilters = () => {
        setSearch("");
        setStatusFilter("all");
        setPage(1);
    };

    const columns = [
        {
            key: "clientID",
            label: "Client ID",
            nowrap: true,
            render: (client) => (
                <span className="font-medium" style={{ color: "var(--brand)" }}>
                    {client.clientID}
                </span>
            ),
        },
        {
            key: "clientName",
            label: "Client Name",
            render: (client) => client.clientName,
        },
        {
            key: "poc",
            label: "Contact Person",
            render: (client) => (
                <span style={{ color: "var(--main-text-muted)" }}>{client.poc?.name || "—"}</span>
            ),
        },
        {
            key: "email",
            label: "Email",
            render: (client) => (
                <span style={{ color: "var(--main-text-muted)" }}>{client.poc?.email || "—"}</span>
            ),
        },
        {
            key: "phone",
            label: "Phone",
            nowrap: true,
            render: (client) => (
                <span style={{ color: "var(--main-text-muted)" }}>
                    {formatPhone(client.poc?.number)}
                </span>
            ),
        },
        {
            key: "country",
            label: "Country",
            nowrap: true,
            render: (client) => (
                <span style={{ color: "var(--main-text-muted)" }}>
                    {client.location?.country || "—"}
                </span>
            ),
        },
        {
            key: "status",
            label: "Status",
            nowrap: true,
            render: (client) => (
                <div className="flex items-center gap-3">
                    <button
                        disabled={isTableLoading}
                        onClick={() => toggleStatus(client)}
                        role="switch"
                        aria-checked={client.isActive}
                        aria-label={client.isActive ? "Deactivate client" : "Activate client"}
                        title={client.isActive ? "Deactivate client" : "Activate client"}
                        className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-300"
                        style={{ background: client.isActive ? "var(--success)" : "var(--danger)" }}
                    >
                        <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-300 ${
                                client.isActive ? "translate-x-6" : "translate-x-1"
                            }`}
                        />
                    </button>

                    <span
                        className="text-xs font-medium min-w-[56px]"
                        style={{ color: client.isActive ? "var(--success)" : "var(--danger)" }}
                    >
                        {client.isActive ? "Active" : "Inactive"}
                    </span>
                </div>
            ),
        },
        {
            key: "actions",
            label: "Actions",
            nowrap: true,
            align: "right",
            render: (client) => (
                <div className="flex items-center justify-end gap-4">
                    <button
                        disabled={!client.isActive}
                        aria-label={`Edit ${client.clientName}`}
                        title={client.isActive ? "Edit client" : "Activate the client to edit it"}
                        onClick={() => {
                            if (!client.isActive) return;
                            setEditingClient(client);
                            setIsModalOpen(true);
                        }}
                        className={
                            client.isActive
                                ? "text-[var(--brand)] hover:text-[var(--brand-dark)]"
                                : "opacity-40 cursor-not-allowed"
                        }
                    >
                        <Edit2 size={16} />
                    </button>

                    {/* Allowed regardless of active state — deactivating is
                        the reversible action, so an inactive client is the
                        one most likely to be deleted. */}
                    <button
                        aria-label={`Delete ${client.clientName}`}
                        title="Delete client"
                        onClick={() => setDeletingClient(client)}
                        className="text-[var(--danger)] hover:opacity-80"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="p-4 sm:p-6 min-h-screen w-full" style={{ background: "var(--main-bg)", color: "var(--main-text)" }}>

            <PageHeader
                icon={<User size={28} strokeWidth={2.2} />}
                title="Clients"
                subtitle="Create, update and manage clients"
                meta={isPageLoading ? null : `${total} ${total === 1 ? "client" : "clients"}`}
                actions={
                    <button
                        onClick={() => {
                            setEditingClient(null);
                            setIsModalOpen(true);
                        }}
                        className="flex items-center gap-2 font-medium px-4 h-10 rounded-lg transition hover:opacity-90"
                        style={{ background: "var(--brand)", color: "#fff" }}
                    >
                        <Plus size={18} /> Create Client
                    </button>
                }
            />

            {/* Filters */}
            <div className="p-4 sm:p-5 rounded-lg mb-6 border" style={{ background: "var(--card-bg)", borderColor: "var(--card-border)" }}>
                <div className="flex flex-col sm:flex-row sm:items-end gap-4">

                    {/* Search */}
                    <div className="flex-1 min-w-0">
                        <label htmlFor="client-search" className="text-sm mb-2 block" style={{ color: "var(--main-text)" }}>
                            Search
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-2.5 pointer-events-none" size={18} style={{ color: "var(--main-text-muted)" }} />
                            <input
                                id="client-search"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full pl-10 pr-9 py-2 h-10 border rounded-lg"
                                style={{
                                    background: "var(--input-background)",
                                    color: "var(--main-text)",
                                    borderColor: "var(--card-border)"
                                }}
                                placeholder="Search by client name, contact name or email"
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => { setSearch(""); setPage(1); }}
                                    aria-label="Clear search"
                                    className="absolute right-2 top-2.5 p-0.5 rounded hover:opacity-70"
                                    style={{ color: "var(--main-text-muted)" }}
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Status */}
                    <div>
                        <label htmlFor="client-status" className="text-sm mb-2 block" style={{ color: "var(--main-text)" }}>
                            Status
                        </label>
                        <select
                            id="client-status"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                            className="w-full sm:w-[140px] px-3 py-2 h-10 border rounded-lg"
                            style={{
                                background: "var(--input-background)",
                                color: "var(--main-text)",
                                borderColor: "var(--card-border)"
                            }}
                        >
                            <option value="all">All</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>

                    {/* Reset — disabled when there is nothing to reset, so the
                        control reflects whether any filter is actually on. */}
                    <button
                        onClick={clearFilters}
                        disabled={!hasActiveFilters}
                        className="flex items-center justify-center gap-2 h-10 px-4 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ background: "var(--brand-light)", color: "var(--brand)" }}
                    >
                        <ListRestart size={16} />
                        Reset
                    </button>
                </div>
            </div>

            <DataTable
                columns={columns}
                rows={clients}
                loading={isPageLoading || isTableLoading}
                getRowKey={(client) => client._id}
                emptyState={
                    hasActiveFilters ? (
                        <EmptyState
                            icon={<Search size={26} />}
                            title="No clients match these filters"
                            description="Try a different search term, or clear the filters to see every client."
                            action={
                                <button
                                    onClick={clearFilters}
                                    className="h-10 px-4 rounded-lg font-medium border transition hover:opacity-80"
                                    style={{ color: "var(--brand)", borderColor: "var(--brand)" }}
                                >
                                    Clear filters
                                </button>
                            }
                        />
                    ) : (
                        <EmptyState
                            icon={<Users size={26} />}
                            title="No clients yet"
                            description="Clients own the licenses you issue. Create the first one to get started."
                            action={
                                <button
                                    onClick={() => {
                                        setEditingClient(null);
                                        setIsModalOpen(true);
                                    }}
                                    className="flex items-center gap-2 h-10 px-4 rounded-lg font-medium"
                                    style={{ background: "var(--brand)", color: "#fff" }}
                                >
                                    <Plus size={18} /> Create Client
                                </button>
                            }
                        />
                    )
                }
            />

            <Pagination
                page={page}
                totalPages={totalPages}
                totalRecords={total}
                onPageChange={setPage}
            />

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
                    <div className="rounded-lg w-full max-w-xl p-6 max-h-[85vh]" style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)" }}>
                        <div className="flex justify-between mb-4">
                            <h3 className="text-lg font-semibold" style={{ color: "var(--main-text)" }}>
                                {editingClient ? "Edit Client" : "Create Client"}
                            </h3>
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setEditingClient(null);
                                }}
                                style={{ color: "var(--main-text-muted)" }}
                                className="hover:text-[var(--main-text)]"
                            >
                                <X />
                            </button>
                        </div>

                        <div
                            style={{ scrollbarGutter: "stable" }}
                            className="max-h-[70vh] overflow-y-auto pr-6 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800"
                        >
                            <ClientForm
                                onSubmit={handleSubmitClient}
                                isLoading={isSubmitting}
                                submitLabel={editingClient ? "Update Client" : "Create Client"}
                                defaultValues={editingClient}
                            />
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={Boolean(deletingClient)}
                title="Delete client"
                message={
                    <>
                        Permanently delete{" "}
                        <strong style={{ color: "var(--main-text)" }}>
                            {deletingClient?.clientName}
                        </strong>
                        ? Deactivate the client instead if you only want to stop new
                        licenses being issued to them.
                    </>
                }
                details={
                    deletingClient && (
                        <div className="space-y-1">
                            <div>Client ID: {deletingClient.clientID}</div>
                            <div>Contact: {deletingClient.poc?.email}</div>
                        </div>
                    )
                }
                confirmLabel="Delete client"
                isLoading={isDeleting}
                onConfirm={handleDeleteClient}
                onCancel={() => setDeletingClient(null)}
            />
        </div>
    );
};

export default Client;
